import { inject, ref, type Ref } from 'vue';
import NxSectionNavRoot from './NxSectionNavRoot.vue';
import NxSectionNavItemSfc from './NxSectionNavItem.vue';
import NxSectionNavListSfc from './NxSectionNavList.vue';
import { sectionItemIdKey, sectionNavRegistryKey, type SectionNavItemTag } from './internal';
import type { DomRegistry, DomRegistryItem } from './createDomRegistry';

/**
 * Creates a generically-typed section-nav system around `createDomRegistry`. Call
 * once (e.g. in a consumer's `<script setup>`). The runtime wiring is the shared
 * module-level key + provide/inject (hierarchy isolates trees); this factory's job
 * is purely to pin the imported generic SFCs to one shared `T`, so every
 * `SectionNavItem` / `SectionNavList` in the tree carries the same metadata type.
 */
export function defineSectionNav<T>() {
  // SectionNavRoot is type-erased (provides registry<unknown>); returned as-is.
  // SectionNavItem/SectionNavList are generic SFCs; pin their T to the factory's T.
  const SectionNavItem = NxSectionNavItemSfc as unknown as new () => {
    $props: {
      id: string;
      meta: T;
      /** What to render as — a tag name or a component. Must have a single element root
       *  (the registry needs a real element to observe + scroll to). Defaults to <div>. */
      is?: SectionNavItemTag;
    };
    $slots: { default: (props: { active: boolean }) => unknown };
  };
  const SectionNavList = NxSectionNavListSfc as unknown as new () => {
    $props: Record<string, never>;
    $slots: {
      default: (props: { items: DomRegistryItem<T>[]; activeId: string | null }) => unknown;
    };
  };

  /** Raw registry access (items / activeId / setActive / register). Throws outside SectionNavRoot. */
  function useSectionNav(): DomRegistry<T> {
    const registry = inject(sectionNavRegistryKey);
    if (!registry) throw new Error('[nx-ui] useSectionNav() must be called inside SectionNavRoot');
    return registry as DomRegistry<T>;
  }

  /** Same as `useSectionNav`, or `null` outside a SectionNavRoot (e.g. isolated stack tests). */
  function tryUseSectionNav(): DomRegistry<T> | null {
    return (inject(sectionNavRegistryKey, null) as DomRegistry<T> | null) ?? null;
  }

  /** The nearest SectionNavItem's id (for descendants); ref('') + dev-warn outside an item. */
  function useSectionItemId(): Ref<string> {
    const injected = inject(sectionItemIdKey, null);
    if (!injected) {
      if (import.meta.env?.DEV) {
        console.warn('[nx-ui] useSectionItemId() was called outside a SectionNavItem.');
      }
      return ref('');
    }
    return injected;
  }

  return {
    SectionNavRoot: NxSectionNavRoot,
    SectionNavItem,
    SectionNavList,
    useSectionNav,
    tryUseSectionNav,
    useSectionItemId,
  };
}
